import {
  ALL_MODULES,
  getTranslatedText,
  ENTITY_MAP,
  COMMON_CONSTANTS
} from '@nile/components';
import {
  AccountClient,
  getApiMethodAndKeyNames,
  apiResolver,
  GET_FIELD,
  GET_REQUESTERS_BY_CRITERIA,
  GET_TECHNICIANS_BY_CRITERIA,
  TicketClient
} from '@nile/graphql';

const DEFAULT_TASK_CONDITION = {
  joinOperator: 'AND',
  operands: [{ attribute: 'taskId', operator: 'isNotEmpty', value: '' }]
};

/**
 * For entity type, if we want to call a specif api, this method will be called.
 * @param {object} item
 */
function getQuery(item) {
  const entity = item?.name?.split('.')[0] || '';
  switch (entity) {
    case 'technician':
    case 'followers':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      return GET_REQUESTERS_BY_CRITERIA;
  }
}

/**
 * After getting values from the API, this value specifies
 * the method name to process the data
 * @param {object} item
 */
function getNameKey(item) {
  const entity = item?.split('.')[0] || '';
  switch (entity) {
    case 'technician':
    case 'followers':
      return 'getTechnicianList';
    default:
      return 'getRequesterList';
  }
}

function formatOperatorValues(allOperators, operator, intl) {
  return allOperators.getRuleBuilderOperators.operator_placeholders[
    operator
  ].map((el) => {
    return {
      ...el,
      label: getTranslatedText(intl, el.key),
      value: getTranslatedText(intl, el.key),
      name: el.key
    };
  });
}

/**
 * This method gives the query and client using which the API calls will be made
 * after selecting the attribute
 * @param {object} item
 * @param {String} operator
 */

function getPropValues(item, operator, allOperators, intl) {
  if (item?.name) {
    if (item.isCustom) {
      if (operator) {
        return formatOperatorValues(allOperators, operator, intl);
      }
      return AccountClient.query({
        query: GET_FIELD,
        variables: {
          input: {
            columnName: item?.name || item?.columnName,
            module: ALL_MODULES.ticket
          }
        }
      });
    }
    const query = apiResolver.Task[item.name]?.api;
    const client = apiResolver.Task[item.name]?.client;
    let variables = apiResolver.Task[item.name]?.variables || {};
    if (item.name === 'client.name' || item.name === 'workItem.client.name') {
      variables = {
        listInfo: {
          sort: {
            attribute: 'name',
            order: 'ASC'
          }
        }
      };
    }
    if (query && client) {
      return client.query({
        query: query,
        variables: variables
      });
    }
    if (operator) {
      return formatOperatorValues(allOperators, operator, intl);
    }
    return { client: AccountClient, query: getQuery(item) };
  }
  return {};
}

async function onAttributeSelect(params) {
  const { item: field, operator, fields, allOperators, intl } = params;
  const allFields = fields;
  const selectedField = allFields.find((el) => el.name === field);
  if (
    selectedField?.name !== 'workItem.client.name' &&
    selectedField?.name !== 'workItem.site.name'
  ) {
    if (field.split('.')?.length > 1) {
      const ent = field.split('.')[0];
      if (ent !== 'techGroup' && ent !== 'client')
        return getPropValues(selectedField, null, allOperators);
    }
  }
  if (selectedField?.dataType === 'boolean') {
    return [
      {
        name: 'true',
        label: 'true',
        value: true
      },
      {
        name: 'false',
        label: 'false',
        value: false
      }
    ];
  }
  const allValues = await getPropValues(
    selectedField,
    operator,
    allOperators,
    intl
  );
  let res = [];

  if (selectedField?.name) {
    const apiMethodAndKeyNames = getApiMethodAndKeyNames(ENTITY_MAP.task);
    let method = apiMethodAndKeyNames[selectedField.name]?.method;
    const key = apiMethodAndKeyNames[selectedField.name]?.key;
    const secondaryMethod =
      apiMethodAndKeyNames[selectedField.name]?.secondaryMethod;
    const nestedKey = apiMethodAndKeyNames[selectedField.name]?.nestedKey;

    let dataToMap = null;
    if (secondaryMethod) {
      dataToMap = allValues.data[method]?.[secondaryMethod];
    } else if (operator === 'on') {
      dataToMap = allValues;
    } else {
      dataToMap = allValues.data?.[method];
    }
    if (selectedField.nestedTo) {
      const nestedProp = apiMethodAndKeyNames[selectedField.name]?.nestedKey;
      if (!dataToMap && !method) {
        dataToMap = allValues.data?.getField?.options;
      }
      dataToMap.forEach((el) => {
        if (el[nestedProp]) {
          res = res.concat(
            el[nestedProp].map((c) => {
              return {
                name: c[key],
                value: c[key],
                label: c[key]
              };
            })
          );
        } else {
          res = res.concat([
            { name: el?.value, value: el?.value, label: el?.value }
          ]);
        }
      });
    } else if (operator === 'on') {
      res = allValues;
    } else if (selectedField.isCustom) {
      method = 'getField';
      const fldOptions = dataToMap?.options || dataToMap?.fieldOptions;
      res = dataToMap
        ? fldOptions?.map((el) => {
            return {
              name: el?.value,
              value: el?.value,
              label: el?.value
            };
          })
        : [];
    } else if (nestedKey) {
      res = dataToMap?.[nestedKey]
        ? dataToMap?.[nestedKey].map((el) => {
            return {
              name: el[key],
              value: el[key],
              label: el[key]
            };
          })
        : [];
    } else {
      res = dataToMap
        ? dataToMap.map((el) => {
            return {
              name: el[key],
              value: el[key],
              label: el[key]
            };
          })
        : [];
    }
  }
  return res;
}

/**
 * This method is called for autocomplete fields.
 * Usually apis will be called when we choose attributes. But for autocomplete,
 * on typing few characters, api will be called. This is the method.
 * @param {object} client
 * @param {gql} query
 * @param {object} params
 */
const onValueSearch = async (client, query, params) => {
  const attribute = params?.field?.split('.')[1];
  const nameKey = getNameKey(params.field);
  const variables = {
    listInfo: {
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: attribute || 'firstName',
            operator: 'contains',
            value: params.inputValue
          }
        ]
      }
    }
  };
  const res = await client.query({
    query: query,
    variables: COMMON_CONSTANTS.IS_INSIDE_INPUT_VARIABLES[nameKey]
      ? { input: variables }
      : variables
  });
  const allOptions = res?.data?.[nameKey]
    ? res.data[nameKey].userList.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
    : [];
  return allOptions;
};

export { onAttributeSelect, onValueSearch, DEFAULT_TASK_CONDITION };
